import { lazy, Suspense, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, errorText } from '../lib/api';
import type { TokenResult } from '../lib/live';
import { Button, ErrorText } from './ui';
import type { StageLayout } from './LiveStage';

const LiveStage = lazy(() => import('./LiveStage'));

/**
 * The join button and, once joined, the media stage. `getToken` asks our API for a short-lived LiveKit token (the API
 * decides what this person may publish). When LiveKit isn't configured the API says so and we show it plainly.
 */
export function LiveJoin({ getToken, layout, label = 'Join', disabledReason, allowShare, onJoined, onLeft }: { getToken: () => Promise<TokenResult>; layout: StageLayout; label?: string; disabledReason?: string | null; allowShare?: boolean; onJoined?: () => void; onLeft?: () => void }) {
  const [session, setSession] = useState<TokenResult | null>(null);
  const join = useMutation({
    mutationFn: getToken,
    onSuccess: (r) => {
      setSession(r);
      onJoined?.();
    },
  });
  if (session)
    return (
      <Suspense fallback={<p className="m-0 text-sub">Loading video…</p>}>
        <LiveStage
          url={session.url}
          token={session.token}
          layout={layout}
          allowShare={allowShare}
          onLeave={() => {
            setSession(null);
            onLeft?.();
          }}
        />
      </Suspense>
    );
  const notSet = join.error instanceof ApiError && join.error.body.code === 'LIVEKIT_NOT_CONFIGURED';
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={() => join.mutate()} disabled={join.isPending || !!disabledReason}>
          {join.isPending ? 'Joining…' : label}
        </Button>
        {disabledReason && <span className="text-sm text-sub">{disabledReason}</span>}
      </div>
      {notSet ? (
        <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">
          {errorText(join.error)}
        </p>
      ) : (
        <ErrorText error={join.error} />
      )}
    </div>
  );
}
